import { MultiSelectOption } from '@/components/ui/multi-select';
import {
  buildCoachSelectOptions,
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
} from '@/components/ui/multi-select-utils';
import {
  EmptyArray,
  getBooks,
  getCoach,
  getCoaches,
  getCoachSessionsByDate,
  getLearningPaths,
  getLesson,
  getLessonsForCoach,
  getLessonsTypeahead,
  getMinimalSchools,
  getOrgs,
  getSchedules,
  getUsersTypeahead,
  listPayCycles,
} from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { fetchAllPages } from '@/lib/pagination';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { FrequentTimeZones, student } from '@airreading/common';
import {
  adjustMinimalClassTimeAcrossTZ,
  allPagesModifier,
  ClassTimeType,
  GetCoachesModifier,
  GetLessonsTypeaheadModifier,
  GetOrgsResults,
  GetPayCyclesResult,
  GetUsersTypeaheadFilter,
  GetUsersTypeaheadModifier,
  MinimalSchoolWithOrg,
  typeaheadPagesModifier,
  UserLike,
} from '@airreading/common/data';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  CoachesTypeaheadSearchOptions,
  SubstituteCoachConflictResult,
  SubstituteCoachConflictsWithSessionProps,
} from './coach-utils';

export function useAllSchedules({ schoolId }: { schoolId?: string } = {}) {
  const { data: allSchedulesResponse, isPending, error } = useQuery(getSchedules({ ...fetchAllPages, schoolId }));
  const allSchedules = useMemo(() => allSchedulesResponse?.schedules, [allSchedulesResponse]);
  return { data: allSchedules, isPending, error };
}

export function useAllBooks() {
  const resp = useQuery(getBooks(fetchAllPages));

  return {
    ...resp,
    books: resp.data?.books || EmptyArray,
  };
}

export function useClassTimeOptions({
  schoolId,
  classTimeTypes = ['recurring'], // by default, we only show recurring class times since one-time class times are not user-facing
}: {
  schoolId: string;
  classTimeTypes?: ClassTimeType[];
}) {
  const schedulesResp = useAllSchedules({ schoolId });
  const schedules = schedulesResp.data || EmptyArray;
  const classTimes = useMemo(
    () =>
      schedules
        .flatMap(({ classTimes, ...otherSchedule }) =>
          classTimes.map((classTime) => ({
            classTime,
            schedule: otherSchedule,
          })),
        )
        .filter(({ classTime: { type } }) => {
          return classTimeTypes.length === 0 || classTimeTypes.includes(type);
        }),
    [classTimeTypes, schedules],
  );

  const classTimeOptions: MultiSelectOption[] = useMemo(() => {
    return classTimes.map(
      ({
        classTime,
        schedule: {
          name: scheduleName,
          school: {
            org: { timeZone },
          },
        },
      }) => ({
        label: `${scheduleName}: ${formatStudentClassTimeAbbr(adjustMinimalClassTimeAcrossTZ(classTime, timeZone, FrequentTimeZones.UsCentral))}`,
        value: classTime.id,
      }),
    );
  }, [classTimes]);

  const classTimeWithSchoolOptions: MultiSelectOption[] = useMemo(() => {
    return classTimes.map(({ classTime, schedule: { name: scheduleName, school } }) => ({
      label: `${school.name} ${scheduleName}: ${formatStudentClassTimeAbbr(adjustMinimalClassTimeAcrossTZ(classTime, school.org.timeZone, FrequentTimeZones.UsCentral))}`,
      value: classTime.id,
    }));
  }, [classTimes]);

  return {
    ...schedulesResp,
    classTimes,
    classTimeOptions,
    classTimeWithSchoolOptions,
  };
}

export function useAllLearningPaths() {
  const resp = useQuery(getLearningPaths());

  return {
    ...resp,
    learningPaths: resp.data || EmptyArray,
  };
}

export function useAllSchools(): {
  data: MinimalSchoolWithOrg[];
  isPending: boolean;
} {
  // Use the minimal schools endpoint instead of the full schools endpoint
  const { data: allSchoolsResponse, isPending } = useQuery(getMinimalSchools(allPagesModifier));
  const allSchools = useMemo(() => allSchoolsResponse?.schools || EmptyArray, [allSchoolsResponse]);
  return { data: allSchools, isPending };
}

export function useSchoolOptions(): MultiSelectOption[] {
  const { data, isPending } = useAllSchools();
  return useMemo(
    () => (isPending ? [] : data?.map((school) => ({ label: school.name, value: school.id })) ?? []),
    [data, isPending],
  );
}

export function useSchoolAndOrgOptions({ orgIds }: { orgIds?: string[] } = {}): MultiSelectOption[] {
  const { data, isPending } = useAllSchools();

  const filteredSchools = useMemo(() => {
    if (!orgIds || orgIds.length === 0) return data;
    return data.filter((school) => orgIds.includes(school.orgId));
  }, [data, orgIds]);

  return useMemo(
    () => (isPending ? [] : filteredSchools?.map((school) => ({ label: school.name, value: school.id })) ?? []),
    [filteredSchools, isPending],
  );
}

export function useAllOrgs(): {
  data: GetOrgsResults['orgs'];
  isPending: boolean;
} {
  const { data: allOrgsResponse, isPending } = useQuery(getOrgs(allPagesModifier));
  const allOrgs = useMemo(() => allOrgsResponse?.orgs || EmptyArray, [allOrgsResponse]);
  return { data: allOrgs, isPending };
}

export function useOrgOptions(): MultiSelectOption[] {
  const { data, isPending } = useAllOrgs();
  return useMemo(
    () => (isPending ? [] : data?.map((org) => ({ label: org.name, value: org.id })) ?? []),
    [data, isPending],
  );
}

export const useAllLessonsForCoach = () => {
  const resp = useQuery({ ...getLessonsForCoach(fetchAllPages), staleTime: 30000 });
  return {
    ...resp,
    lessons: resp.data?.lessons || EmptyArray,
  };
};

export const lessonTypeaheadSearch = (queryClient: QueryClient) => {
  return async (typeahead: string) => {
    const req: GetLessonsTypeaheadModifier = {
      ...typeaheadPagesModifier,
      filters: {
        typeahead,
      },
    };

    return queryClient
      .fetchQuery({
        ...getLessonsTypeahead(req),
        staleTime: 30000,
        queryKey: ['LESSONS_TYPEAHEAD_OPTIONS', req],
      })
      .then((results) => {
        return results.map(buildNameLikeSelectOptions);
      });
  };
};

export const userTypeaheadSearch = (
  queryClient: QueryClient,
  filtersWithoutTypeahead?: Omit<GetUsersTypeaheadFilter, 'typeahead'>,
) => {
  return async (typeahead: string): Promise<MultiSelectOption[]> => {
    const modifier = {
      ...typeaheadPagesModifier,
      filters: {
        typeahead,
        ...filtersWithoutTypeahead,
      },
    } satisfies GetUsersTypeaheadModifier;

    return queryClient
      .fetchQuery({
        ...getUsersTypeahead(modifier),
        staleTime: 30000,
        queryKey: ['users', modifier],
      })
      .then((results) => {
        return results.map(buildUserSelectOptions);
      });
  };
};

export const coachesTypeaheadSearch = (queryClient: QueryClient, options?: CoachesTypeaheadSearchOptions) => {
  return async (typeahead: string): Promise<MultiSelectOption[]> => {
    const modifier = {
      ...typeaheadPagesModifier,
      filters: {
        query: typeahead,
        ...(options?.filters || {}),
      },
    } satisfies GetCoachesModifier;

    return queryClient
      .fetchQuery({
        ...getCoaches(modifier),
        staleTime: 30000,
        queryKey: ['AVAILABLE_COACHES', modifier],
      })
      .then((results) => {
        if (options?.shouldDisplayOverallGrade) {
          return results.coaches.map(buildCoachSelectOptions);
        }

        return results.coaches.map(({ id, fullNameOrEmail }) =>
          buildUserSelectOptions({ id, fullNameOrEmail } as UserLike),
        );
      });
  };
};

export const studentsTypeaheadSearch = (
  queryClient: QueryClient,
  filtersWithoutTypeahead?: Omit<GetUsersTypeaheadFilter, 'typeahead'>,
) => {
  return userTypeaheadSearch(queryClient, { roles: [student().role], ...filtersWithoutTypeahead });
};

export function useRequiredOnboardingLessonsData(lessonName: string, learningPathName: string) {
  const { learningPaths = EmptyArray, isPending: isLoading, error } = useAllLearningPaths();
  useErrorToast(error, 'Error fetching learning paths/lessons when generating onboarding sessions');
  const learningPath = learningPaths.find((path) => path.name === learningPathName);
  const startingLesson = learningPath?.learningPathLessons.find(({ lesson }) => lesson.name === lessonName)?.lesson;
  const missingPredefinedLessons = !startingLesson || !learningPath;

  return {
    startingLesson,
    learningPath,
    missingPredefinedLessons,
    isLoading,
  };
}

export const useAvailableCoachesSearch = (modifier?: GetCoachesModifier, options?: CoachesTypeaheadSearchOptions) => {
  return useQuery({
    queryKey: ['AVAILABLE_COACHES', modifier],
    queryFn: async () => {
      const coachesQuery = getCoaches(modifier);
      const results = await coachesQuery.queryFn();

      if (options?.shouldDisplayOverallGrade) {
        return results.coaches.map(buildCoachSelectOptions);
      }

      return results.coaches.map(({ id, fullNameOrEmail }) =>
        buildUserSelectOptions({ id, fullNameOrEmail } as UserLike),
      );
    },
    staleTime: 30000,
    enabled: !!modifier,
  });
};

export async function getSubstituteCoachConflictsWithSession({
  originalCoachId,
  substituteCoachId,
  session,
  queryClient,
  start,
  end,
}: SubstituteCoachConflictsWithSessionProps): Promise<SubstituteCoachConflictResult> {
  const hasTeacherChange = originalCoachId !== substituteCoachId;
  const coachOptions = {
    coachId: substituteCoachId,
    start: (start?.toInstant() || session.startInstant).toString(),
    end: (end?.toInstant() || session.endInstant).toString(),
  };

  const coachClasses = hasTeacherChange
    ? await queryClient.fetchQuery({
        queryKey: getCoachSessionsByDate.queryKeyFn(coachOptions),
        queryFn: () => getCoachSessionsByDate.queryFn(coachOptions),
      })
    : {
        coachSessions: [],
      };

  //Remove the current session from the list of conflicts
  const coachSessions = coachClasses.coachSessions.filter((s) => s.id !== session.id);
  const coachHasConflicts = coachSessions.length > 0;
  return { coachHasConflicts, coachSessions };
}

/**
 * Custom hook for fetching coach data by ID
 * Returns a function that fetches coach data and normalizes the response
 */
export function useCoachById() {
  const queryClient = useQueryClient();

  const fetchCoachById = useCallback(
    async (coachId: string) => {
      const queryConfig = getCoach(coachId);
      const result = (await queryClient.fetchQuery(queryConfig)) as { fullNameOrEmail: string; id: string };
      return { name: result.fullNameOrEmail, id: result.id };
    },
    [queryClient],
  );

  return fetchCoachById;
}

/**
 * Custom hook for fetching lesson data by ID
 * Returns a function that fetches lesson data and normalizes the response
 */
export function useLessonById() {
  const queryClient = useQueryClient();

  const fetchLessonById = useCallback(
    async (lessonId: string) => {
      const queryConfig = getLesson(lessonId);
      const result = (await queryClient.fetchQuery(queryConfig)) as { name: string; id: string };
      return { name: result.name, id: result.id };
    },
    [queryClient],
  );

  return fetchLessonById;
}

export function usePayCycleOptions(): MultiSelectOption[] {
  const { data: payCycleOptions, isPending } = useQuery(listPayCycles());

  const formatOptions = (payCycle: GetPayCyclesResult): MultiSelectOption => {
    const { startPlainDate, endPlainDate } = payCycle;

    const startDate = startPlainDate.toLocaleString('en-US', { month: 'short' });
    const endDate = endPlainDate.toLocaleString('en-US', { month: 'short' });

    return {
      label: `${startDate} ${startPlainDate.day} - ${endDate} ${endPlainDate.day}, ${endPlainDate.year}`,
      value: `${startPlainDate}/${endPlainDate}`,
    };
  };

  return useMemo(() => (isPending ? [] : payCycleOptions?.map(formatOptions) ?? []), [payCycleOptions, isPending]);
}
